import React, { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Share2, MessageCircle, Copy, Check, MoreHorizontal } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { whatsappUrl, trackShare } from '../lib/share';
import './ShareMenu.css';

/**
 * Share to WhatsApp (Oct 2026). One "Share" button → WhatsApp · Copy message ·
 * More options (the phone's own share sheet, where the device has one).
 *
 * WhatsApp is a plain wa.me link opened like the map links: on a phone it
 * opens the WhatsApp app with the message typed in and the person picks the
 * chat/group; on a desktop it opens WhatsApp Web. Inside the native app the
 * WebView hands non-app URLs to the OS, which gives wa.me to WhatsApp.
 *
 * `text` must already contain the link (lib/share.js builds both together),
 * so every channel sends the identical message.
 */

export async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* fall through — blocked on http or by permissions */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

const hasSystemShare = () => Capacitor.isNativePlatform() || typeof navigator.share === 'function';

async function systemShare(text) {
  if (Capacitor.isNativePlatform()) {
    await Share.share({ text, dialogTitle: 'Share' });
  } else {
    await navigator.share({ text });
  }
}

/** Standalone "WhatsApp" button — for invite links, beside an existing Copy. */
export function WhatsAppButton({ text, clubSlug, kind = 'invite', targetId = null, className = 'btn btn-secondary', label = 'WhatsApp', size = 14 }) {
  const { user } = useAuth();
  return (
    <a
      href={whatsappUrl(text)}
      target="_blank"
      rel="noopener noreferrer"
      className={`${className} share-wa-btn`}
      style={{ textDecoration: 'none' }}
      onClick={(e) => {
        e.stopPropagation();
        if (user) trackShare(clubSlug, { kind, targetId, channel: 'whatsapp' });
      }}
    >
      <MessageCircle size={size} /> {label}
    </a>
  );
}

export default function ShareMenu({ text, clubSlug, kind, targetId = null, label = 'Share', className = 'btn btn-secondary btn-sm', align = 'left', iconOnly = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef(null);

  // Close on any click outside THIS menu — including opening another one
  // (a stopPropagation-based close let two menus stay open at once).
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  const track = (channel) => { if (user) trackShare(clubSlug, { kind, targetId, channel }); };

  const onCopy = async (e) => {
    e.stopPropagation();
    if (await copyText(text)) {
      track('copy');
      setCopied(true);
      setTimeout(() => { setCopied(false); setOpen(false); }, 1200);
    } else {
      window.prompt('Copy this message:', text);
    }
  };

  const onMore = async (e) => {
    e.stopPropagation();
    setOpen(false);
    try {
      await systemShare(text);
      track('system');
    } catch { /* dismissed the sheet — nothing to do */ }
  };

  return (
    <div className="share-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className={className}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={iconOnly ? label : undefined}
        title={iconOnly ? label : undefined}
        onClick={() => setOpen(o => !o)}
      >
        <Share2 size={14} />{!iconOnly && <> {label}</>}
      </button>
      {open && (
        <div className={`share-menu-popover share-menu-${align}`} role="menu">
          <a
            role="menuitem"
            className="share-menu-item"
            href={whatsappUrl(text)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.stopPropagation(); track('whatsapp'); setOpen(false); }}
          >
            <MessageCircle size={15} /> WhatsApp
          </a>
          <button type="button" role="menuitem" className="share-menu-item" onClick={onCopy}>
            {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy message</>}
          </button>
          {hasSystemShare() && (
            <button type="button" role="menuitem" className="share-menu-item" onClick={onMore}>
              <MoreHorizontal size={15} /> More options…
            </button>
          )}
        </div>
      )}
    </div>
  );
}
